import React from 'react';
import { View, Text, StyleSheet, Image } from 'react-native';
import { AntDesign } from '@expo/vector-icons';
import AvatarHandler from '@components/Global/AvatarHandler/AvatarHandler';

interface UserProfile {
  name?: string;
  surname?: string;
  avatar?: any;
}

interface User {
  _id: string;
  email?: string;
  profile_information?: UserProfile;
  department?: string;
  role?: string;
  status?: string;
  type?: string;
  isAgent?: boolean;
  agentType?: string;
  agentMode?: string;
}

interface UserCardProps {
  user: User;
  theme: boolean;
  isDragging: boolean;
  showArrows?: boolean;
  portrait?: boolean;
}

const UserCard: React.FC<UserCardProps> = ({
  user,
  theme,
  isDragging,
  showArrows = true,
  portrait = true,
}) => {


  const getName = () => {
    if (!user) return 'No User Data';
    const profile = user.profile_information || user as any;
    const name = `${profile?.name || 'Unknown'} ${profile?.surname || ''}`.trim();
    return name === 'Unknown' ? 'Current User' : name;
  };

  const getAvatar = () => {
    if (!user) return null;
    // For agents, use agent avatar
    if (user.isAgent || user.type === 'agent') {
      return user.profile_information?.avatar?.img || null;
    }
    const profile = user.profile_information || user as any;
    return profile?.avatar?.img || null;
  };

  const getInitials = () => {
    const name = getName();
    if (name === 'No User Data') return '?';
    return name
      .split(' ')
      .map(word => word.charAt(0))
      .join('')
      .toUpperCase()
      .substring(0, 2);
  };

  const isAgent = user.isAgent || user.type === 'agent';
  const agentTypeColors = {
    business_developer: theme ? '#3b82f6' : '#2563eb',
    sales_marketer: theme ? '#10b981' : '#059669',
    researcher: theme ? '#8b5cf6' : '#7c3aed'
  };
  const agentColor = isAgent && user.agentType ? agentTypeColors[user.agentType] : undefined;

  return (
    <View style={[
      styles.card,
      {
        backgroundColor: theme ? '#ffffff' : '#2d3748',
        borderColor: isDragging ? '#3b82f6' : (isAgent && agentColor ? agentColor : (theme ? '#e2e8f0' : '#4a5568')),
        borderWidth: isAgent ? 3 : 2,
        opacity: isDragging ? 0.9 : 1,
        ...(isAgent && {
          shadowColor: agentColor || '#000',
          shadowOffset: { width: 0, height: 2 },
          shadowOpacity: 0.2,
          shadowRadius: 4,
          elevation: 6,
        })
      }
    ]}>
      {/* Avatar */}
      <View style={styles.avatarContainer}>
        <AvatarHandler
          avatar={getAvatar()}
          avatarImageStyle={styles.avatar}
          fallbackInitials={getInitials()}
          fallbackStyle={[
            styles.avatarPlaceholder,
            { backgroundColor: theme ? '#e2e8f0' : '#4a5568' }
          ]}
          fallbackTextStyle={[
            styles.avatarText,
            { color: theme ? '#2d3748' : '#ffffff' }
          ]}
        />
      </View>

      {/* User Info */}
      <View style={styles.userInfo}>
        <Text style={[
          styles.userName,
          { color: theme ? '#2d3748' : '#ffffff' }
        ]}>
          {getName()}
        </Text>

        {user.email && (
          <Text style={[
            styles.userEmail,
            { color: theme ? '#718096' : '#a0aec0' }
          ]}>
            {user.email}
          </Text>
        )}

        {user.department && (
          <Text style={[
            styles.userDepartment,
            { color: theme ? '#4a5568' : '#cbd5e0' }
          ]}>
            {user.department}
          </Text>
        )}

        {user.role && (
          <Text style={[
            styles.userRole,
            { color: theme ? '#805ad5' : '#b794f6' }
          ]}>
            {user.role}
          </Text>
        )}
        
        {/* Agent Badge */}
        {isAgent && (
          <View style={[
            styles.agentBadge,
            { backgroundColor: agentColor || (theme ? '#3b82f6' : '#2563eb') }
          ]}>
            <AntDesign name="robot" size={12} color="white" />
            <Text style={styles.agentBadgeText}>
              {user.agentType?.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'Agent'}
            </Text>
          </View>
        )}
      </View>

      {/* Status Indicator */}
      {user.status && (
        <View style={[
          styles.statusIndicator,
          {
            backgroundColor: user.status === 'active' ? '#48bb78' :
              user.status === 'pending' ? '#ed8936' : '#e53e3e'
          }
        ]} />
      )}

      {/* Drag Handle */}
      {showArrows && (
        <View style={styles.dragHandle}>
          <AntDesign
            name="drag"
            size={16}
            color={theme ? '#a0aec0' : '#718096'}
          />
        </View>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  card: {
    width: 200,
    height: 120,
    borderRadius: 12,
    borderWidth: 2,
    padding: 12,
    flexDirection: 'row',
    alignItems: 'center',
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 2,
    },
    shadowOpacity: 0.25,
    shadowRadius: 3.84,
    elevation: 5,
  },
  avatarContainer: {
    marginRight: 12,
  },
  avatar: {
    width: 48,
    height: 48,
    borderRadius: 24,
  },
  avatarPlaceholder: {
    width: 48,
    height: 48,
    borderRadius: 24,
    justifyContent: 'center',
    alignItems: 'center',
  },
  avatarText: {
    fontSize: 16,
    fontWeight: 'bold',
  },
  userInfo: {
    flex: 1,
    justifyContent: 'center',
  },
  userName: {
    fontSize: 14,
    fontWeight: 'bold',
    marginBottom: 2,
  },
  userEmail: {
    fontSize: 12,
    marginBottom: 2,
  },
  userDepartment: {
    fontSize: 11,
    fontStyle: 'italic',
  },
  userRole: {
    fontSize: 10,
    marginTop: 2,
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  statusIndicator: {
    position: 'absolute',
    top: 8,
    right: 8,
    width: 8,
    height: 8,
    borderRadius: 4,
  },
  dragHandle: {
    position: 'absolute',
    bottom: 8,
    right: 8,
  },
  agentBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
    marginTop: 4,
    alignSelf: 'flex-start',
  },
  agentBadgeText: {
    color: '#ffffff',
    fontSize: 10,
    fontWeight: '600',
    textTransform: 'capitalize',
  },
});

export default UserCard;
